import isAuth from "@lib/utils/isAuth";
import { ScopeUnavailableError } from "@sdk/spellbook";

export type AccessScope = "authenticated" | "account" | "network" | "accountOrNetwork";

export class AccessNotReadyError extends Error {
    constructor(scope: AccessScope) {
        super(`Access scope is not ready: ${scope}`);
        this.name = "AccessNotReadyError";
    }
}

export class AuthenticationRequiredError extends Error {
    constructor() {
        super("Authentication required");
        this.name = "AuthenticationRequiredError";
    }
}

export const ACCESS_STORAGE_KEYS = {
    activeOrganisationId: "side_team_activeOrganisationId",
    networkId: "side_team_groupId",
    userId: "Meteor.userId",
    logasId: "side_team_logasId",
    loginToken: "Meteor.loginToken",
} as const;

export function getStoredValue(key: string): string | null {
    const value = localStorage.getItem(key)?.trim();
    return value || null;
}

/**
 * Returns the cache identity for a scope only when the current browser session
 * has all the information required to make requests in that scope.
 */
export function getAccessScopeId(scope: AccessScope): string | null {
    if (!isAuth()) return null;

    switch (scope) {
        case "authenticated":
            return getStoredValue(ACCESS_STORAGE_KEYS.userId);
        case "account":
            return getStoredValue(ACCESS_STORAGE_KEYS.activeOrganisationId);
        case "network":
            return getStoredValue(ACCESS_STORAGE_KEYS.networkId);
        case "accountOrNetwork":
            return (
                getStoredValue(ACCESS_STORAGE_KEYS.activeOrganisationId) ??
                getStoredValue(ACCESS_STORAGE_KEYS.networkId)
            );
    }
}

/** Throws a recognizable, non-user-facing error before an ineligible request is built. */
export function requireAccessScope(scope: AccessScope): string {
    const scopeId = getAccessScopeId(scope);
    if (!scopeId) {
        throw new AccessNotReadyError(scope);
    }
    return scopeId;
}

function isErrorInCauseChain(
    error: unknown,
    isExpectedError: (error: object) => boolean,
    errorName: string,
    messagePrefix: string,
): boolean {
    const visited = new Set<unknown>();
    let current = error;

    while (current && !visited.has(current)) {
        if (
            typeof current === "string" &&
            (current.startsWith(`${errorName}:`) || current.startsWith(messagePrefix))
        ) {
            return true;
        }
        if (typeof current !== "object") return false;
        if (isExpectedError(current)) return true;
        visited.add(current);
        current = "cause" in current ? current.cause : undefined;
    }

    return false;
}

export function isAccessNotReadyError(error: unknown): boolean {
    return isErrorInCauseChain(
        error,
        (current) =>
            current instanceof ScopeUnavailableError || current instanceof AccessNotReadyError,
        AccessNotReadyError.name,
        "Access scope is not ready:",
    );
}

export function isAuthenticationRequiredError(error: unknown): boolean {
    return isErrorInCauseChain(
        error,
        (current) =>
            current instanceof AuthenticationRequiredError ||
            ("response" in current &&
                !!current.response &&
                typeof current.response === "object" &&
                "status" in current.response &&
                current.response.status === 401),
        AuthenticationRequiredError.name,
        "Authentication required:",
    );
}

/** True for local access-scope failures and authentication failures that should use the fallback route. */
export function isAccessRedirectError(error: unknown): boolean {
    return isAccessNotReadyError(error) || isAuthenticationRequiredError(error);
}

/**
 * Adds a scope identity to a query key and disables the query until the
 * corresponding access context exists. Callers can combine `enabled` with
 * their own input checks (for example, a non-empty search string).
 * Suspense queries must also be protected by a route scope because TanStack's
 * `useSuspenseQuery` always enables its query.
 */
export function queryAccess(scope: AccessScope, queryKey: readonly unknown[]) {
    const userId = getAccessScopeId("authenticated");
    const scopeId = getAccessScopeId(scope);
    const logasId = userId ? getStoredValue(ACCESS_STORAGE_KEYS.logasId) : null;

    return {
        queryKey: [...queryKey, "access", scope, userId, scopeId, logasId],
        enabled: scopeId !== null,
    } as const;
}

/** Keeps pagination placeholders only when both queries use the same access context. */
export function keepPreviousDataForSameAccessScope<T>(
    previousData: T | undefined,
    previousQueryKey: readonly unknown[] | undefined,
    currentQueryKey: readonly unknown[],
): T | undefined {
    const currentAccessIndex = currentQueryKey.lastIndexOf("access");
    const previousAccessIndex = previousQueryKey?.lastIndexOf("access") ?? -1;
    if (!previousQueryKey || currentAccessIndex < 0 || previousAccessIndex < 0) return undefined;

    const currentAccess = currentQueryKey.slice(currentAccessIndex);
    const previousAccess = previousQueryKey.slice(previousAccessIndex);
    return currentAccess.length === previousAccess.length &&
        currentAccess.every((part, index) => Object.is(part, previousAccess[index]))
        ? previousData
        : undefined;
}
